import React from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, Calendar, Award, User, Sparkles, GraduationCap, BookOpen, Users, Phone, Globe, Instagram, Facebook, Youtube, Linkedin } from 'lucide-react';
import { Badge } from './ui/badge';
import { getLocalizedText, formatPrice, formatDate } from '../utils/helpers';
import { GoogleRatingDisplay } from './GoogleRatingDisplay';

// Quick contact button component
const QuickContactButton = ({ href, icon: Icon, label, color = "gray" }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    onClick={(e) => e.stopPropagation()}
    className={`p-2 rounded-full bg-${color}-100 hover:bg-${color}-200 transition-colors`}
    title={label}
    style={{ backgroundColor: color === 'primary' ? 'rgba(212, 165, 116, 0.2)' : undefined }}
  >
    <Icon className="h-4 w-4" style={{ color: color === 'primary' ? '#D4A574' : '#666' }} />
  </a>
);

// Social icon mapping
const socialIcons = {
  instagram: Instagram,
  facebook: Facebook,
  youtube: Youtube,
  linkedin: Linkedin,
};

// Target audience labels
const audienceLabels = {
  all: 'Todos',
  women: 'Mujeres',
  men: 'Hombres', 
  couples: 'Parejas',
  singles: 'Singles',
  lgbtq: 'LGBTQ+',
  lgbtqia: 'LGBTQIA+'
};

export function RatingStars({ rating, count }) {
  return (
    <div className="flex items-center space-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${star <= Math.round(rating) ? 'fill-gold text-gold' : 'text-gray-300'}`}
          style={star <= Math.round(rating) ? { fill: '#D4A574', color: '#D4A574' } : {}}
        />
      ))}
      {count !== undefined && <span className="text-sm text-secondary ml-1">({count})</span>}
    </div>
  );
}

export function ServiceCard({ service, language = 'es' }) {
  const hasImage = service.images && service.images.length > 0;
  const hasGoogleRating = service.google_rating && service.google_rating > 0;
  
  return (
    <Link to={`/service/${service.id}`} data-testid={`service-card-${service.id}`}>
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm card-hover h-full">
        <div className="relative h-48 bg-gradient-to-br from-cream-dark to-cream flex items-center justify-center overflow-hidden" style={{ background: 'linear-gradient(to bottom right, #F5EFE6, #FDFBF7)' }}>
          {hasImage ? (
            <img src={service.images[0]} alt={getLocalizedText(service.title, language)} className="w-full h-full object-cover" />
          ) : (
            <Sparkles className="h-16 w-16 text-gold opacity-50" style={{ color: '#D4A574' }} />
          )}
          {/* Google Rating Badge */}
          {hasGoogleRating && (
            <div className="absolute top-2 right-2">
              <GoogleRatingDisplay 
                rating={service.google_rating}
                reviewsCount={service.google_reviews_count}
                isVerified={true}
                variant="badge"
              />
            </div>
          )}
        </div>
        <div className="p-5">
          {service.is_featured && <Badge className="mb-2 bg-gold text-white" style={{ backgroundColor: '#D4A574' }}>Destacado</Badge>}
          <h3 className="font-serif text-lg font-semibold text-primary mb-1">{getLocalizedText(service.title, language)}</h3>
          <p className="text-sm text-secondary mb-2">{service.teacher_name}</p>
          <div className="flex items-center text-sm text-secondary mb-2">
            <MapPin className="h-4 w-4 mr-1" />
            <span>{service.location?.city || 'Online'}</span>
          </div>
          <div className="flex items-center justify-between mt-3">
            <span className="font-semibold text-gold" style={{ color: '#D4A574' }}>{formatPrice(service.price, service.currency)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export function CourseCard({ course, language = 'es' }) {
  const schoolName = course.school_name ? getLocalizedText(course.school_name, language) : course.teacher_name;
  const hasImage = course.images && course.images.length > 0;
  const hasGoogleRating = course.google_rating && course.google_rating > 0;
  
  // Format date range
  const formatDateRange = () => {
    if (!course.start_date) return 'Fechas flexibles';
    const startFormatted = formatDate(course.start_date);
    if (course.end_date && course.end_date !== course.start_date) {
      const endFormatted = formatDate(course.end_date);
      return `${startFormatted} - ${endFormatted}`;
    }
    return startFormatted;
  };
  
  // Get audience display
  const getAudienceDisplay = () => {
    if (!course.target_audience || course.target_audience.length === 0) return null;
    if (course.target_audience.includes('all')) return null;
    return course.target_audience.map(a => audienceLabels[a] || a).join(', ');
  };
  
  const audienceDisplay = getAudienceDisplay();
  
  return (
    <Link to={`/course/${course.id}`} data-testid={`course-card-${course.id}`}>
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm card-hover h-full">
        <div className="relative h-48 bg-gradient-to-br from-cream-dark to-cream flex items-center justify-center overflow-hidden" style={{ background: 'linear-gradient(to bottom right, #F5EFE6, #FDFBF7)' }}>
          {hasImage ? (
            <img src={course.images[0]} alt={getLocalizedText(course.title, language)} className="w-full h-full object-cover" />
          ) : (
            <GraduationCap className="h-16 w-16 text-gold opacity-50" style={{ color: '#D4A574' }} />
          )}
          {/* Google Rating Badge */}
          {hasGoogleRating && (
            <div className="absolute top-2 right-2">
              <GoogleRatingDisplay 
                rating={course.google_rating}
                reviewsCount={course.google_reviews_count}
                isVerified={true}
                variant="badge"
              />
            </div>
          )}
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {course.featured && <Badge className="bg-gold text-white" style={{ backgroundColor: '#D4A574' }}>Destacado</Badge>}
            {course.is_online && <Badge variant="outline">Online</Badge>}
            {course.is_residential && <Badge variant="outline" className="border-green-500 text-green-600">Residencial</Badge>}
          </div>
          <h3 className="font-serif text-lg font-semibold text-primary mb-1">{getLocalizedText(course.title, language)}</h3>
          <p className="text-sm text-secondary mb-2">{schoolName}</p>
          {/* Location - visible on all devices */}
          {course.location && (course.location.city || course.location.country) && !course.is_online && (
            <div className="flex items-center text-sm text-secondary mb-1">
              <MapPin className="h-4 w-4 mr-1 flex-shrink-0 text-primary/70" />
              <span className="truncate">
                {[course.location.city, course.location.province || course.location.country].filter(Boolean).join(', ')}
              </span>
            </div>
          )}
          {course.is_online && (
            <div className="flex items-center text-sm text-secondary mb-1">
              <MapPin className="h-4 w-4 mr-1 flex-shrink-0 text-primary/70" />
              <span>Online</span>
            </div>
          )}
          <div className="flex items-center text-sm text-secondary mb-1">
            <Calendar className="h-4 w-4 mr-1 flex-shrink-0" />
            <span>{formatDateRange()}</span>
          </div>
          {audienceDisplay && (
            <div className="flex items-center text-sm text-secondary mb-1">
              <Users className="h-4 w-4 mr-1 flex-shrink-0" />
              <span>{audienceDisplay}</span>
            </div>
          )}
          <div className="flex items-center justify-between mt-3">
            <span className="font-semibold text-gold" style={{ color: '#D4A574' }}>{formatPrice(course.price, course.currency)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export function TeacherCard({ teacher, language = 'es' }) {
  const hasImage = teacher.images && teacher.images.length > 0;
  const googleRating = teacher.google_business_rating;
  const hasGoogleRating = googleRating?.is_verified && googleRating?.google_rating > 0;
  
  // Check for quick contacts
  const hasPhone = teacher.phone && teacher.phone.trim();
  const hasWebsite = teacher.website && teacher.website.trim();
  const socialLinks = teacher.social_links || {};
  const hasSocials = Object.values(socialLinks).some(v => v && v.trim());
  const hasQuickContacts = hasPhone || hasWebsite || hasSocials;
  
  return (
    <Link to={`/teacher/${teacher.id}`} data-testid={`teacher-card-${teacher.id}`}>
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm card-hover h-full">
        <div className="relative h-56 bg-gradient-to-br from-cream-dark to-cream flex items-center justify-center" style={{ background: 'linear-gradient(to bottom right, #F5EFE6, #FDFBF7)' }}>
          {hasImage ? (
            <img 
              src={teacher.images[0]} 
              alt={teacher.user_name} 
              className="w-full h-full object-contain"
              style={{ backgroundColor: '#F5EFE6' }}
            />
          ) : (
            <User className="h-16 w-16 text-gold opacity-50" style={{ color: '#D4A574' }} />
          )}
          {/* Google Rating Badge */}
          {hasGoogleRating && (
            <div className="absolute top-2 right-2">
              <GoogleRatingDisplay 
                rating={googleRating.google_rating}
                reviewsCount={googleRating.google_reviews_count}
                isVerified={true}
                variant="badge"
              />
            </div>
          )}
        </div>
        <div className="p-5">
          <h3 className="font-serif text-lg font-semibold text-primary mb-1">{teacher.user_name}</h3>
          <p className="text-sm text-secondary mb-2 line-clamp-2">{getLocalizedText(teacher.bio, language)}</p>
          <div className="flex items-center text-sm text-secondary mb-2">
            <MapPin className="h-4 w-4 mr-1" />
            <span>{teacher.location?.city}</span>
          </div>
          <div className="flex items-center text-sm text-secondary mb-3">
            <Award className="h-4 w-4 mr-1" />
            <span>{teacher.experience_years} anos experiencia</span>
          </div>
          
          {/* Quick Contacts */}
          {hasQuickContacts && (
            <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
              {hasPhone && (
                <a
                  href={`tel:${teacher.phone}`}
                  onClick={(e) => e.stopPropagation()}
                  className="p-2 rounded-full bg-green-100 hover:bg-green-200 transition-colors"
                  title="Llamar"
                >
                  <Phone className="h-4 w-4 text-green-600" />
                </a>
              )}
              {hasWebsite && (
                <a
                  href={teacher.website.startsWith('http') ? teacher.website : `https://${teacher.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 transition-colors"
                  title="Sitio web"
                >
                  <Globe className="h-4 w-4 text-blue-600" />
                </a>
              )}
              {Object.entries(socialLinks).map(([key, url]) => {
                if (!url || !url.trim()) return null;
                const Icon = socialIcons[key];
                if (!Icon) return null;
                return (
                  <a
                    key={key}
                    href={url.startsWith('http') ? url : `https://${url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="p-2 rounded-full bg-pink-100 hover:bg-pink-200 transition-colors"
                    title={key.charAt(0).toUpperCase() + key.slice(1)}
                  >
                    <Icon className="h-4 w-4 text-pink-600" />
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

export function SchoolCard({ school, language = 'es' }) {
  const hasImage = school.images && school.images.length > 0;
  const googleRating = school.google_business_rating;
  const hasGoogleRating = googleRating?.is_verified && googleRating?.google_rating > 0;
  
  // Check for quick contacts
  const hasPhone = school.phone && school.phone.trim();
  const hasWebsite = school.website && school.website.trim();
  const socialLinks = school.social_links || {};
  const hasSocials = Object.values(socialLinks).some(v => v && v.trim());
  const hasQuickContacts = hasPhone || hasWebsite || hasSocials;
  
  return (
    <Link to={`/school/${school.id}`} data-testid={`school-card-${school.id}`}>
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm card-hover h-full">
        <div className="relative h-56 bg-gradient-to-br from-cream-dark to-cream flex items-center justify-center" style={{ background: 'linear-gradient(to bottom right, #F5EFE6, #FDFBF7)' }}>
          {hasImage ? (
            <img 
              src={school.images[0]} 
              alt={getLocalizedText(school.name, language)} 
              className="w-full h-full object-contain"
              style={{ backgroundColor: '#F5EFE6' }}
            />
          ) : (
            <BookOpen className="h-16 w-16 text-gold opacity-50" style={{ color: '#D4A574' }} />
          )}
          {/* Google Rating Badge */}
          {hasGoogleRating && (
            <div className="absolute top-2 right-2">
              <GoogleRatingDisplay 
                rating={googleRating.google_rating}
                reviewsCount={googleRating.google_reviews_count}
                isVerified={true}
                variant="badge"
              />
            </div>
          )}
        </div>
        <div className="p-5">
          <h3 className="font-serif text-lg font-semibold text-primary mb-1">{getLocalizedText(school.name, language)}</h3>
          <p className="text-sm text-secondary mb-2 line-clamp-2">{getLocalizedText(school.description, language)}</p>
          <div className="flex items-center text-sm text-secondary mb-3">
            <MapPin className="h-4 w-4 mr-1" />
            <span>{school.location?.city}</span>
          </div>
          
          {/* Quick Contacts */}
          {hasQuickContacts && (
            <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
              {hasPhone && (
                <a
                  href={`tel:${school.phone}`}
                  onClick={(e) => e.stopPropagation()}
                  className="p-2 rounded-full bg-green-100 hover:bg-green-200 transition-colors"
                  title="Llamar"
                >
                  <Phone className="h-4 w-4 text-green-600" />
                </a>
              )}
              {hasWebsite && (
                <a
                  href={school.website.startsWith('http') ? school.website : `https://${school.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 transition-colors"
                  title="Sitio web"
                >
                  <Globe className="h-4 w-4 text-blue-600" />
                </a>
              )}
              {Object.entries(socialLinks).map(([key, url]) => {
                if (!url || !url.trim()) return null;
                const Icon = socialIcons[key];
                if (!Icon) return null;
                return (
                  <a
                    key={key}
                    href={url.startsWith('http') ? url : `https://${url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="p-2 rounded-full bg-pink-100 hover:bg-pink-200 transition-colors"
                    title={key.charAt(0).toUpperCase() + key.slice(1)}
                  >
                    <Icon className="h-4 w-4 text-pink-600" />
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
